import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Network, Plus, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Tribu() {
  const [tribu, setTribu] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const load = () => api.get("/tribu").then((r) => setTribu(r.data));
  useEffect(() => { load(); }, []);

  const search = async () => {
    const { data } = await api.get("/companies/search", { params: q ? { q } : {} });
    setResults(data);
  };

  const add = async (c) => {
    try {
      await api.post("/tribu/members", { company_id: c.id });
      toast.success(`${c.company_name} ajouté à la Tribu`);
      load(); search();
    } catch (err) { toast.error(apiError(err)); }
  };

  const memberIds = new Set((tribu?.members || []).map((m) => m.id));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Network className="h-6 w-6 text-primary" /> {tribu?.name || "Ma Tribu Hospitalière"}
        </h1>
        <p className="text-muted-foreground text-sm">Vous êtes le chef de votre Tribu : ajoutez vos transporteurs de confiance en un clic, sans vote.</p>
      </div>

      <Card className="p-4 rounded-md shadow-none">
        <h2 className="font-display font-semibold mb-3">Transporteurs de confiance ({tribu?.members?.length || 0})</h2>
        {(tribu?.members || []).length === 0 && <p className="text-sm text-muted-foreground">Aucun transporteur. Ajoutez-en ci-dessous.</p>}
        <div className="space-y-2" data-testid="tribu-members">
          {(tribu?.members || []).map((m) => (
            <div key={m.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2" data-testid={`tribu-member-${m.id}`}>
              <div>
                <p className="font-medium text-sm">{m.company_name}</p>
                <p className="text-xs text-muted-foreground">{m.email}</p>
              </div>
              <Badge variant="outline" className="border-green-500 text-green-600 gap-1" data-testid={`tribu-member-badge-${m.id}`}>
                <CheckCircle2 className="h-4 w-4" /> Membre
              </Badge>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 rounded-md shadow-none">
        <h2 className="font-display font-semibold mb-3">Ajouter un transporteur</h2>
        <div className="flex gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Nom de l'entreprise ou email exact" className="pl-9" data-testid="tribu-search-input" />
          </div>
          <Button onClick={search} data-testid="tribu-search-btn">Rechercher</Button>
        </div>
        <div className="mt-3 space-y-2">
          {results.map((c) => (
            <div key={c.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2">
              <div>
                <p className="font-medium text-sm">{c.company_name}</p>
                <p className="text-xs text-muted-foreground">{c.rate_per_km?.toFixed?.(2) ?? c.rate_per_km} €/km · prise en charge {c.base_fare} €</p>
              </div>
              {memberIds.has(c.id) ? (
                <Badge variant="outline" className="border-green-500 text-green-600">Déjà membre</Badge>
              ) : (
                <Button size="sm" className="gap-1" onClick={() => add(c)} data-testid={`tribu-add-${c.id}`}><Plus className="h-4 w-4" /> Ajouter</Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
